import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useContext, useMemo, useState } from 'react'
import dayjs from 'shared/functions/dayjs'
import { AuthStateContext } from 'context/AuthStateContext'
import { NotyfContext } from 'context/NotyfContext'
import { useUserChallenges } from 'hooks/useUserChallenges'
import { useReservations } from 'hooks/useReservations'
import { ChallengeType, CreateUserChallengeMutationOptions } from 'services/api/types'
import { Challenge } from 'types'
import { ReactComponent as PhilIllustration } from 'assets/images/ondemand-filter-header.svg'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

type ScheduleLegacyUserChallengeModalProps = {
  challenge: Challenge
  setIsOpen: (open: boolean) => void
  isInAChallenge?: boolean
}

export const ScheduleLegacyUserChallengeModal = ({ challenge, setIsOpen, isInAChallenge = false }: ScheduleLegacyUserChallengeModalProps) => {
  const notyf = useContext(NotyfContext)
  const { state: authState } = useContext(AuthStateContext)
  const { create, createChallengeLoading } = useUserChallenges()
  const { createReservation, creatingReservation } = useReservations()
  const [withMusic, setWithMusic] = useState(true)
  const userId = authState.user?.id

  const isBusy = createChallengeLoading || creatingReservation

  const today = dayjs().format('YYYY-MM-DD')
  const releaseDate = dayjs(challenge.releaseDate).format('YYYY-MM-DD')
  const earliestDateForRes = today > releaseDate ? today : releaseDate

  const [selectedDate, setSelectedDate] = useState(earliestDateForRes)
  const [includeDownloadWeek, setIncludeDownloadWeek] = useState(false)

  const daysFromNow = useMemo(() => {
    return dayjs(selectedDate).startOf('day').diff(dayjs().startOf('day'), 'days')
  }, [selectedDate])

  const showDownloadWeek = useMemo(() => {
    return daysFromNow >= 1 && daysFromNow <= 7
  }, [daysFromNow])

  const creatingAUc = useMemo(() => {
    return (includeDownloadWeek && daysFromNow <= 7) || daysFromNow < 1
  }, [includeDownloadWeek, daysFromNow])

  const dateRequiresWarning = isInAChallenge && creatingAUc

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value
    setSelectedDate(selected)
  }

  // Build options payload for the standard "legacy" challenge
  const buildOptions = (): CreateUserChallengeMutationOptions => {
    const isoStartDate = dayjs(selectedDate).startOf('day').toISOString()

    return {
      challengeType: ChallengeType.Legacy,
      userId: userId || '',
      challengeId: challenge.id,
      startDate: isoStartDate,
      withMusic,
      redirect: true,
    }
  }

  // Start Challenge Immediately
  const handleStartNow = async () => {
    if (!userId) {
      notyf.error('Please login to start a challenge')
      return
    }
    try {
      await create(buildOptions())
      setIsOpen(false)
    } catch (err) {
      console.error('Error creating user challenge:', err)
      notyf.error('Challenge creation failed')
    }
  }

  const determineButtonText = () => {
    const daysFromNow = dayjs(selectedDate).startOf('day').diff(dayjs().startOf('day'), 'days')
    const formatedDate = dayjs(selectedDate).format('MMM D')
    switch (true) {
      case isBusy:
        return 'Working...'
      case dateRequiresWarning:
        return `Cancel and start on ${formatedDate}`
      case daysFromNow < 1 && isInAChallenge:
        return 'Cancel and Start Now'
      case daysFromNow < 1:
        return 'Start Now'
      case includeDownloadWeek && showDownloadWeek && isInAChallenge:
        return `Cancel and Start on ${formatedDate}`
      default:
        return `Start on ${formatedDate}`
    }
  }

  /**
   * Creates a reservation for a scheduled challenge start.
   * @returns {Promise<void>}
   */
  const scheduleReservation = async () => {
    const options = buildOptions()
    const preferences = {
      withMusic: options.withMusic,
      challengeId: options.challengeId,
      startDate: options.startDate,
    }
    try {
      const redirect = true
      const newRes = await createReservation({ ...options, preferences, redirect })
      if (newRes) {
        notyf.success('Reservation created')
        setIsOpen(false)
      }
    } catch (error) {
      console.error('Error creating reservation:', error)
      notyf.error('Failed to create reservation')
    }
  }

  const handleStartChallenge = async () => {
    if ((daysFromNow <= 7 && includeDownloadWeek) || daysFromNow < 1) {
      // Start immediately
      await handleStartNow()
    } else {
      // Create reservation
      await scheduleReservation()
    }
  }

  const disableMusic = useMemo(() => {
    if (!challenge.hasMusic) {
      setWithMusic(false)
      return true
    }
    return false
  }, [challenge])

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-200" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-99 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-200 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:w-full sm:max-w-sm">
                <div className="text-center">
                  <h2 className="text-2xl font-extrabold">{challenge.title}</h2>
                  <h4 className="text-xl font-bold mt-2">Challenge Preferences</h4>

                  {/* Music Toggle */}
                  <div className="bg-slate-50 rounded-md p-3 mb-2">
                    <p className="font-bold mb-2">Would you like music?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        className={`h-12 transition-all justify-center rounded-lg font-bold text-md ${
                          withMusic ? 'bg-coral text-white hover:bg-coral-dark ring-2 ring-slate-500' : 'bg-slate-100 text-slate-800 border border-slate-500'
                        } ${disableMusic ? 'opacity-50 cursor-not-allowed disabled:hover:bg-slate-100' : ''}`}
                        disabled={disableMusic}
                        onClick={() => setWithMusic(true)}
                      >
                        Music
                      </button>
                      <button
                        type="button"
                        className={`h-12 transition-all justify-center rounded-lg font-bold text-md ${
                          !withMusic ? 'bg-coral text-white hover:bg-coral-dark ring-2 ring-slate-500' : 'bg-slate-100 text-slate-800 border border-slate-500'
                        }`}
                        onClick={() => setWithMusic(false)}
                      >
                        No Music
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-md p-4 my-4">
                  <label className="block text-center font-semibold mb-4 text-lg">When would you like to start?</label>

                  <div className="text-center w-full">
                    <label className="block mb-2 text-gray-700 font-medium">Pick a Start Date:</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded-md focus:ring-coral-dark focus:border-coral"
                      value={selectedDate}
                      onClick={(e) => {
                        const input = e.target as HTMLInputElement
                        if (document.activeElement === input) {
                          input.showPicker?.()
                        }
                      }}
                      onChange={handleDateChange}
                      min={earliestDateForRes}
                    />
                  </div>

                  {showDownloadWeek && (
                    <div className="mt-4 transition-opacity duration-300 ease-in-out">
                      <label className="flex items-center gap-4">
                        <span className="text-gray-700 font-lg">Prep with download week workouts?</span>
                        <div className="relative">
                          <input type="checkbox" checked={includeDownloadWeek} onChange={(e) => setIncludeDownloadWeek(e.target.checked)} className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer-focus:ring-2 peer-focus:ring-coral-dark peer-checked:bg-coral-dark" />
                          <div className="absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow-md transform peer-checked:translate-x-5 transition-transform" />
                        </div>
                      </label>
                    </div>
                  )}

                  {dateRequiresWarning && (
                    <div className="mt-.1 flex items-center gap-4 rounded-lg bg-gray-100 p-4 border border-gray-300 shadow-sm">
                      <ExclamationTriangleIcon className="w-6 h-6 stroke-yellow-500" strokeWidth={2} />
                      <span className="italic text-red-600">{'This will end your current challenge'}</span>
                    </div>
                  )}
                  <button className="w-full h-12 bg-coral text-white rounded-lg font-bold mt-2 hover:bg-coral-dark" onClick={handleStartChallenge} disabled={isBusy}>
                    {determineButtonText()}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
